import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../lib/authStore';
import { 
  Menu, 
  X, 
  ChevronRight, 
  User as UserIcon, 
  ExternalLink,
  Shield,
  Search,
  Phone,
  Mail,
  BookOpen
} from 'lucide-react';

interface NavbarProps {
  onOpenMobileDrawer?: () => void;
}

export const Navbar: React.FC<NavbarProps> = ({ onOpenMobileDrawer }) => {
  const { user, isAuthenticated, isStaff } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const navLinks = [
    { label: 'Services', path: '/services' },
    { label: 'Our Process', path: '/our-process' },
    { label: 'Books', path: '/publications' },
    { label: 'For Authors', path: '/for-authors' },
    { label: 'Resources', path: '/resources' },
    { label: 'About', path: '/about' },
    { label: 'Contact', path: '/contact' },
  ];

  const isActive = (path: string) => {
    if (path === '/' && location.pathname === '/') return true;
    if (path !== '/' && location.pathname.startsWith(path)) return true;
    return false;
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;
    navigate(`/publications?search=${encodeURIComponent(searchQuery.trim())}`);
    setSearchOpen(false);
  };

  return (
    <>
      {/* Top Organization & Contact Bar - Ultra Slim & Elegant */}
      <div className="bg-slate-950 text-slate-400 text-[11px] py-1.5 px-4 sm:px-6 lg:px-8 border-b border-slate-800/80 font-sans tracking-tight">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          
          {/* Left: Ecosystem Indicator */}
          <div className="flex items-center gap-2">
            <span className="font-semibold text-brand-400 uppercase tracking-widest text-[9.5px]">
              For The Truth
            </span>
            <span className="text-slate-600 hidden sm:inline">&bull;</span>
            <span className="text-slate-400 hidden sm:inline">
              Publishing House &amp; Author Platform
            </span>
          </div>

          {/* Right: Direct Contact & Bookstore Cross-link */}
          <div className="flex items-center gap-4 sm:gap-6 text-[11px]">
            <a
              href="tel:+918604685533"
              className="flex items-center gap-1.5 text-slate-300 hover:text-white transition-colors"
              title="Call Publishing Office"
            >
              <Phone className="w-3 h-3 text-brand-400" />
              <span>+91 86046 85533</span>
            </a>

            <span className="text-slate-700 hidden sm:inline">|</span>

            <a
              href="mailto:contact@forthetruth.in"
              className="hidden sm:flex items-center gap-1.5 text-slate-300 hover:text-white transition-colors"
              title="Email Editorial Office"
            >
              <Mail className="w-3 h-3 text-brand-400" />
              <span>contact@forthetruth.in</span>
            </a>

            <span className="text-slate-700">|</span>

            <a
              href="https://forthetruth.in/books"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-brand-300 hover:text-brand-200 transition-colors font-medium"
            >
              <span>Visit Bookstore</span>
              <ExternalLink className="w-3 h-3 opacity-70" />
            </a>
          </div>

        </div>
      </div>

      {/* Main Publishing Header - Slim, Non-Clunky, Premium */}
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-slate-200/90 shadow-xs transition-all">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-18">
            
            {/* Official Logo & Brand Title */}
            <Link to="/" className="flex items-center gap-3 group shrink-0">
              <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full overflow-hidden border border-brand-500/40 bg-white p-0.5 shadow-2xs group-hover:border-brand-600 transition-all flex items-center justify-center">
                <img
                  src="/ftt-logo.png"
                  alt="For The Truth Logo"
                  className="w-full h-full object-contain rounded-full"
                />
              </div>
              <div className="flex flex-col justify-center">
                <div className="flex items-center gap-1.5">
                  <span className="font-extrabold text-sm sm:text-base tracking-tight text-slate-900 group-hover:text-brand-700 transition-colors">
                    FOR THE TRUTH
                  </span>
                  <span className="text-[9px] font-mono font-bold tracking-widest text-brand-700 uppercase bg-brand-50 px-1.5 py-0.5 rounded border border-brand-200/60">
                    Publishing
                  </span>
                </div>
                <span className="text-[10px] text-slate-500 font-medium tracking-normal hidden md:inline -mt-0.5">
                  Theological &bull; Scholarly &bull; Enduring
                </span>
              </div>
            </Link>

            {/* Streamlined Desktop Navigation Links */}
            <nav className="hidden lg:flex items-center gap-1">
              {navLinks.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold tracking-tight transition-colors ${
                    isActive(item.path)
                      ? 'text-brand-700 bg-brand-50 font-bold'
                      : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100/80'
                  }`}
                >
                  {item.label}
                </Link>
              ))}
            </nav>

            {/* Desktop Action Buttons */}
            <div className="hidden lg:flex items-center gap-2.5">
              {/* Search Toggle */}
              <button
                onClick={() => setSearchOpen(!searchOpen)}
                className="p-2 rounded-lg text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors"
                title="Search Publications & Services"
                aria-label="Search"
              >
                <Search className="w-4 h-4" />
              </button>

              {/* Portal Login / Dashboard Button */}
              {isAuthenticated ? (
                <Link
                  to={isStaff ? '/admin' : '/client/dashboard'}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-slate-900 text-white hover:bg-slate-800 shadow-2xs transition-all active:scale-95"
                >
                  {isStaff ? <Shield className="w-3.5 h-3.5 text-brand-400" /> : <UserIcon className="w-3.5 h-3.5 text-brand-400" />}
                  <span>{isStaff ? 'Admin' : 'Portal'}</span>
                </Link>
              ) : (
                <Link
                  to="/login"
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold text-slate-700 hover:text-slate-900 hover:bg-slate-100 transition-colors"
                >
                  Author Login
                </Link>
              )}

              {/* Sleek High-Conversion RFQ Button */}
              <Link
                to="/submit-enquiry"
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold bg-brand-600 hover:bg-brand-700 text-white shadow-xs hover:shadow-sm transition-all active:scale-95"
              >
                <span>Start RFQ</span>
                <ChevronRight className="w-3.5 h-3.5 opacity-80" />
              </Link>
            </div>

            {/* Mobile Actions Header */}
            <div className="flex lg:hidden items-center gap-2">
              <Link
                to="/submit-enquiry"
                className="px-3 py-1.5 rounded-lg text-xs font-bold bg-brand-600 text-white shadow-2xs"
              >
                Start RFQ
              </Link>
              <button
                onClick={onOpenMobileDrawer}
                className="p-2 rounded-lg text-slate-700 hover:text-slate-900 hover:bg-slate-100 active:scale-95"
                aria-label="Open Navigation Drawer"
              >
                <Menu className="w-5 h-5" />
              </button>
            </div>

          </div>
        </div>

        {/* Quick Search Dropdown */}
        {searchOpen && (
          <div className="border-t border-slate-200 bg-slate-50/95 p-3 shadow-md animate-in slide-in-from-top-2">
            <form onSubmit={handleSearchSubmit} className="max-w-2xl mx-auto flex items-center gap-2">
              <Search className="w-4 h-4 text-slate-400 shrink-0" />
              <input
                type="text"
                autoFocus
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search published books, ISBNs, authors, services, or guides..."
                className="flex-1 text-xs px-3 py-2 rounded-lg border border-slate-300 bg-white focus:outline-hidden focus:ring-2 focus:ring-brand-500 text-slate-900 font-medium"
              />
              <button
                type="submit"
                className="px-4 py-2 rounded-lg bg-brand-600 text-white text-xs font-bold hover:bg-brand-700 transition-colors"
              >
                Search
              </button>
              <button
                type="button"
                onClick={() => setSearchOpen(false)}
                className="p-2 text-slate-400 hover:text-slate-700"
              >
                <X className="w-4 h-4" />
              </button>
            </form>
          </div>
        )}
      </header>
    </>
  );
};
